import { useEffect, useState } from "react";
import {
  Box,
  FormControlLabel,
  Link,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { GetPublicCall, UpsertPublicKeyCall } from "../../redux/api/public.api";

const HIDE_ANATOMY_KEY = "hide_anatomy";
const ANATOMY_URL_KEY = "anatomy_url";
const DEFAULT_ANATOMY_URL = "https://dashboard.microlab.help/public/anatomy";

function rowData(rows, key) {
  const row = (Array.isArray(rows) ? rows : []).find((r) => r?.key === key);
  return row?.data;
}

function isTruthyFlag(raw) {
  if (raw == null) return false;
  if (typeof raw === "boolean") return raw;
  const s = String(raw).trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes" || s === "on";
}

function upsertOk(res) {
  return res?.status === 200 || res?.status === 201;
}

export default function AnatomyExplorerSettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [showToStudents, setShowToStudents] = useState(true);
  const [explorerUrl, setExplorerUrl] = useState(DEFAULT_ANATOMY_URL);
  const [viewerNonce, setViewerNonce] = useState(0);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const publicRes = await GetPublicCall();
      if (!mounted) return;
      const rows = Array.isArray(publicRes?.data) ? publicRes.data : [];
      setShowToStudents(!isTruthyFlag(rowData(rows, HIDE_ANATOMY_KEY)));
      const url = String(rowData(rows, ANATOMY_URL_KEY) || "").trim();
      setExplorerUrl(url || DEFAULT_ANATOMY_URL);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const onToggleStudents = async (nextValue) => {
    setShowToStudents(nextValue);
    setMessage("");
    const res = await UpsertPublicKeyCall({
      key: HIDE_ANATOMY_KEY,
      data: nextValue ? "false" : "true",
    });
    if (!upsertOk(res)) {
      setShowToStudents(!nextValue);
      setMessage("Could not save the student visibility flag.");
      return;
    }
    setViewerNonce((n) => n + 1);
    setMessage(
      nextValue
        ? "Anatomy is on for students. Course and Quiz now show the 3D viewer."
        : "Anatomy is hidden from students. Admins still see it.",
    );
  };

  const onSaveUrl = async () => {
    setSaving(true);
    setMessage("");
    const trimmed = explorerUrl.trim() || DEFAULT_ANATOMY_URL;
    const res = await UpsertPublicKeyCall({
      key: ANATOMY_URL_KEY,
      data: trimmed,
    });
    setSaving(false);
    if (upsertOk(res)) {
      setExplorerUrl(trimmed);
      setViewerNonce((n) => n + 1);
      setMessage("Explorer URL saved.");
    } else {
      setMessage("Could not save the explorer URL.");
    }
  };

  const previewUrl = (explorerUrl.trim() || DEFAULT_ANATOMY_URL).replace(/\/+$/, "");

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={3} pt={3} pb={3}>
        <Typography variant="h5" fontWeight={700}>
          Anatomy explorer
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 3, maxWidth: 720 }}>
          Turn this on to show the 3D anatomy viewer to every student in
          the Course and Quiz sections of the app. Admins and university
          admins always see Anatomy, even when this switch is off.
        </Typography>

        <Box
          sx={{
            maxWidth: 960,
            p: 2.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <FormControlLabel
            disabled={loading}
            control={
              <Switch
                checked={showToStudents}
                onChange={(e) => onToggleStudents(e.target.checked)}
                color="info"
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  Show Anatomy to all students
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {showToStudents
                    ? "Students see Anatomy in Course and Quiz, and tap it to open this viewer."
                    : "Students do not see Anatomy. Turn this on to enable it in the student app."}
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", ml: 0, mb: 2.5 }}
          />

          <TextField
            fullWidth
            label="Explorer URL"
            value={explorerUrl}
            onChange={(e) => setExplorerUrl(e.target.value)}
            helperText="Public row anatomy_url. Students open this URL inside the app WebView."
            disabled={loading}
            sx={{ mb: 2 }}
          />

          <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <MDButton
              variant="gradient"
              color="info"
              onClick={onSaveUrl}
              disabled={loading || saving}
            >
              {saving ? "Saving…" : "Save URL"}
            </MDButton>
            <Link href={previewUrl} target="_blank" rel="noreferrer">
              Open current explorer
            </Link>
          </MDBox>

          {message ? (
            <Typography variant="body2" sx={{ mt: 2 }}>
              {message}
            </Typography>
          ) : null}

          {showToStudents ? (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                Student viewer
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                This is the same 3D explorer students get in the app when Anatomy
                is enabled.
              </Typography>
              <Box
                sx={{
                  height: { xs: 420, md: 560 },
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "#061116",
                }}
              >
                <iframe
                  key={`${previewUrl}-${viewerNonce}`}
                  title="Anatomy explorer preview"
                  src={previewUrl}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: 0,
                    display: "block",
                  }}
                  allow="fullscreen; xr-spatial-tracking"
                />
              </Box>
            </Box>
          ) : null}
        </Box>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
